import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, DatePipe } from '@angular/common';

// Custom validator for date range
export function dateRangeValidator(group: AbstractControl): ValidationErrors | null {
  if (!group) return null;
  
  const formGroup = group as FormGroup;
  const start = formGroup.get('startDate')?.value;
  const end = formGroup.get('endDate')?.value;
  
  if (start && end && new Date(start) >= new Date(end)) {
    return { 'dateRange': true };
  }
  
  return null;
}

interface Campaign {
  id: number;
  name: string;
  startDate: Date | string;
  endDate: Date | string;
  offerLayout: string;
  status: string;
  isActive: boolean;
  description?: string;
}

@Component({
    selector: 'app-litmus-intraction-campaign',
    templateUrl: './litmus-intraction-campaign.component.html',
    styleUrl: './litmus-intraction-campaign.component.scss',
    imports: [FormsModule, NgIf, NgFor, NgClass, ReactiveFormsModule, NgSwitch, NgSwitchCase, DatePipe]
})
export class LitmusIntractionCampaignComponent implements OnInit, OnDestroy {
  // Properties for campaign management
  campaigns: Campaign[] = [];
  filteredCampaigns: Campaign[] = [];
  loading: boolean = true;
  searchText: string = '';
  
  // Form properties
  campaignForm: FormGroup;
  showCampaignForm: boolean = false;
  isEditMode: boolean = false;
  currentCampaignId: number | null = null;
  
  // Search timer reference
  private searchInterval: any;

  constructor(private fb: FormBuilder) {
    this.campaignForm = this.createCampaignForm();
  }

  ngOnInit(): void {
    // Simulate loading campaigns from API
    setTimeout(() => {
      this.loadDummyCampaigns();
      this.loading = false;
    }, 1000);
    
    // Watch for search text changes
    this.setupSearchWatcher();
  }
  
  ngOnDestroy(): void {
    // Clean up interval to prevent memory leaks
    if (this.searchInterval) {
      clearInterval(this.searchInterval);
    }
  }

  // Create campaign form with validators
  createCampaignForm(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      offerLayout: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      description: ['']
    }, { validators: dateRangeValidator });
  }

  // Load dummy campaigns for demo
  loadDummyCampaigns(): void {
    this.campaigns = [
      {
        id: 1,
        name: 'Summer Sale 2023',
        startDate: '2023-06-01',
        endDate: '2023-08-31',
        offerLayout: 'Grid',
        status: 'Active',
        isActive: true,
        description: 'Summer promotion with special discounts'
      },
      {
        id: 2,
        name: 'Holiday Special',
        startDate: '2023-12-01',
        endDate: '2023-12-31',
        offerLayout: 'Carousel',
        status: 'Scheduled',
        isActive: false,
        description: 'End of year holiday promotions'
      },
      {
        id: 3,
        name: 'Back to School',
        startDate: '2023-08-15',
        endDate: '2023-09-15',
        offerLayout: 'List',
        status: 'Draft',
        isActive: false,
        description: 'Education-related offers and discounts'
      },
      {
        id: 4,
        name: 'Spring Flash Sale',
        startDate: '2023-03-01',
        endDate: '2023-03-15',
        offerLayout: 'Grid',
        status: 'Expired',
        isActive: false,
        description: 'Limited time spring offers'
      }
    ];
    
    this.filteredCampaigns = [...this.campaigns];
  }

  // Setup search watcher
  setupSearchWatcher(): void {
    // This would typically use a Subject/Observable with debounceTime
    // Simplified for demonstration
    this.searchInterval = setInterval(() => {
      this.filterCampaigns();
    }, 300);
  }
  
  // Filter campaigns based on search text
  filterCampaigns(): void {
    if (!this.campaigns || this.campaigns.length === 0) return;
    
    if (!this.searchText) {
      this.filteredCampaigns = [...this.campaigns];
    } else {
      const search = this.searchText.toLowerCase();
      this.filteredCampaigns = this.campaigns.filter(campaign => 
        campaign.name.toLowerCase().includes(search) || 
        campaign.status.toLowerCase().includes(search) ||
        campaign.offerLayout.toLowerCase().includes(search)
      );
    }
  }

  // Get CSS class for campaign status badge
  getCampaignStatusClass(campaign: Campaign): string {
    switch (campaign.status.toLowerCase()) {
      case 'active':
        return 'bg-success';
      case 'draft':
        return 'bg-secondary';
      case 'scheduled':
        return 'bg-primary';
      case 'expired':
        return 'bg-danger';
      default:
        return 'bg-info';
    }
  }

  // Create new campaign handler
  createNewCampaign(): void {
    this.isEditMode = false;
    this.currentCampaignId = null;
    this.campaignForm.reset();
    this.showCampaignForm = true;
  }

  // Edit campaign handler
  editCampaign(campaign: Campaign): void {
    this.isEditMode = true;
    this.currentCampaignId = campaign.id;
    
    // Populate form with campaign data
    this.campaignForm.patchValue({
      name: campaign.name,
      offerLayout: campaign.offerLayout,
      startDate: typeof campaign.startDate === 'string' ? campaign.startDate : campaign.startDate.toISOString().split('T')[0],
      endDate: typeof campaign.endDate === 'string' ? campaign.endDate : campaign.endDate.toISOString().split('T')[0],
      description: campaign.description || ''
    });
    
    this.showCampaignForm = true;
  }

  // Save campaign (create new or update existing)
  saveCampaign(): void {
    if (this.campaignForm.invalid) {
      return;
    }
    
    const formData = this.campaignForm.value;
    
    if (this.isEditMode && this.currentCampaignId) {
      // Update existing campaign
      const index = this.campaigns.findIndex(c => c.id === this.currentCampaignId);
      if (index !== -1) {
        this.campaigns[index] = {
          ...this.campaigns[index],
          name: formData.name,
          offerLayout: formData.offerLayout,
          startDate: formData.startDate,
          endDate: formData.endDate,
          description: formData.description
        };
      }
    } else {
      // Create new campaign
      const newId = Math.max(0, ...this.campaigns.map(c => c.id)) + 1;
      const newCampaign: Campaign = {
        id: newId,
        name: formData.name,
        offerLayout: formData.offerLayout,
        startDate: formData.startDate,
        endDate: formData.endDate,
        description: formData.description,
        status: 'Draft',
        isActive: false
      };
      
      this.campaigns.unshift(newCampaign);
    }
    
    // Reset search to show all campaigns including the new one
    this.searchText = '';
    this.filterCampaigns();
    this.cancelCampaignForm();
  }

  // Cancel form
  cancelCampaignForm(): void {
    this.showCampaignForm = false;
    this.isEditMode = false;
    this.currentCampaignId = null;
    this.campaignForm.reset();
  }

  // Duplicate campaign
  duplicateCampaign(campaign: Campaign): void {
    const newId = Math.max(0, ...this.campaigns.map(c => c.id)) + 1;
    const duplicatedCampaign: Campaign = {
      ...campaign,
      id: newId,
      name: `Copy of ${campaign.name}`,
      status: 'Draft',
      isActive: false
    };
    
    this.campaigns.unshift(duplicatedCampaign);
    this.filterCampaigns();
  }

  // Toggle campaign status
  toggleCampaignStatus(campaign: Campaign): void {
    const index = this.campaigns.findIndex(c => c.id === campaign.id);
    if (index !== -1) {
      this.campaigns[index].isActive = !this.campaigns[index].isActive;
      this.campaigns[index].status = this.campaigns[index].isActive ? 'Active' : 'Draft';
    }
  }

  // Delete campaign
  deleteCampaign(campaign: Campaign): void {
    if (confirm(`Are you sure you want to delete campaign "${campaign.name}"?`)) {
      this.campaigns = this.campaigns.filter(c => c.id !== campaign.id);
      this.filterCampaigns();
    }
  }

  // Check if campaign can be edited
  canEditCampaign(campaign: Campaign): boolean {
    // In a real app, this would use more complex business logic
    // For demo, we'll say active campaigns can't be edited
    return campaign.status.toLowerCase() !== 'active';
  }

  // Check if campaign can be deleted
  canDeleteCampaign(campaign: Campaign): boolean {
    // In a real app, this would use more complex business logic
    // For demo, we'll say active campaigns can't be deleted
    return campaign.status.toLowerCase() !== 'active';
  }
}
